import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import CampusPhoneVerification from '../CampusPhoneVerification';

interface BasicInfoEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (data: { name: string; birth: string; phone: string }) => void;
  initialData?: {
    name: string;
    birth: string;
    phone: string;
  };
}

// 본인확인 결과 타입
interface PhoneVerificationResult {
  code: number;
  status: string;
  message: string;
  data: {
    name: string;
    birth: string;   // YYYY-MM-DD 형식
    phone: string;
  };
}

const BasicInfoEditModal: React.FC<BasicInfoEditModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  initialData
}) => {
  const [formData, setFormData] = useState({
    name: '',
    birthYear: '',
    birthMonth: '',
    birthDay: ''
  });
  
  const [dropdownStates, setDropdownStates] = useState({
    year: false,
    month: false,
    day: false
  });
  
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [phoneVerificationData, setPhoneVerificationData] = useState<PhoneVerificationResult['data'] | null>(null);

  useEffect(() => {
    if (initialData && isOpen) {
      // 초기 데이터 설정
      const birthParts = initialData.birth?.split('-') || [];
      setFormData({
        name: initialData.name || '',
        birthYear: birthParts[0] || '',
        birthMonth: birthParts[1] ? parseInt(birthParts[1], 10).toString() : '',
        birthDay: birthParts[2] ? parseInt(birthParts[2], 10).toString() : ''
      });
      setIsPhoneVerified(false);
      setPhoneVerificationData(null);
    }
  }, [initialData, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 드롭다운 토글
  const handleDropdownToggle = (dropdown: keyof typeof dropdownStates) => {
    setDropdownStates(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  // 드롭다운 선택
  const handleDropdownSelect = (dropdown: keyof typeof dropdownStates, value: string) => {
    if (dropdown === 'year') {
      setFormData(prev => ({ ...prev, birthYear: value }));
    } else if (dropdown === 'month') {
      setFormData(prev => ({ ...prev, birthMonth: value }));
    } else if (dropdown === 'day') {
      setFormData(prev => ({ ...prev, birthDay: value }));
    }
    
    setDropdownStates(prev => ({ ...prev, [dropdown]: false }));
  };

  // 휴대폰 본인확인 완료 처리
  const handlePhoneVerificationComplete = (result: any) => {
    console.log('휴대폰 인증 결과:', result);
    
    if (result.code === 200 && result.data) {
      setIsPhoneVerified(true);
      setPhoneVerificationData(result.data);
      
      // 인증된 데이터로 폼 자동 입력
      if (result.data.name) {
        setFormData(prev => ({ ...prev, name: result.data.name }));
      }
      
      // 생년월일 파싱 및 자동 입력
      if (result.data.birth) {
        const [year, month, day] = result.data.birth.split('-');
        setFormData(prev => ({ 
          ...prev, 
          birthYear: year,
          birthMonth: parseInt(month, 10).toString(),
          birthDay: parseInt(day, 10).toString()
        }));
      }
      
      console.log('본인인증 완료 - 데이터 자동 입력 완료');
      
      // 인증 완료 후 자동으로 API 호출
      const birthDate = result.data.birth;
      const phoneNumber = result.data.phone.replace(/-/g, '');
      
      onComplete({
        name: result.data.name,
        birth: birthDate,
        phone: phoneNumber
      });
    } else {
      setIsPhoneVerified(false);
      setPhoneVerificationData(null);
      console.error('휴대폰 인증 실패:', result.message);
    }
  };

  // 년/월/일 옵션 생성
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderSpacer />
          <CloseButton onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <TitleSection>
            <Title>기본 정보 수정</Title>
            <Subtitle>실명 확인을 위해 휴대폰 본인인증이 필요합니다.</Subtitle>
          </TitleSection>

          <FormSection>
            {/* 이름 입력 */}
            <FormGroup>
              <FormLabel>이름</FormLabel>
              <FormInput
                type="text"
                name="name"
                placeholder="이름을 입력해주세요."
                value={formData.name}
                onChange={handleInputChange}
                disabled={isPhoneVerified}
                className={isPhoneVerified ? 'verified' : ''}
              />
            </FormGroup>

            {/* 생년월일 */}
            <FormGroup>
              <FormLabel>생년월일</FormLabel>
              <BirthContainer>
                <DropdownContainer>
                  <DropdownButton 
                    type="button"
                    onClick={() => !isPhoneVerified && handleDropdownToggle('year')}
                    $isOpen={dropdownStates.year}
                    $hasValue={!!formData.birthYear}
                    disabled={isPhoneVerified}
                  >
                    <span>{formData.birthYear || '연'}</span>
                    <ChevronIcon $isOpen={dropdownStates.year}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ChevronIcon>
                  </DropdownButton>
                  {dropdownStates.year && !isPhoneVerified && (
                    <DropdownMenu>
                      {years.map(year => (
                        <DropdownItem
                          key={year}
                          onClick={() => handleDropdownSelect('year', year.toString())}
                        >
                          {year}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownContainer>

                <DropdownContainer>
                  <DropdownButton 
                    type="button"
                    onClick={() => !isPhoneVerified && handleDropdownToggle('month')}
                    $isOpen={dropdownStates.month}
                    $hasValue={!!formData.birthMonth}
                    disabled={isPhoneVerified}
                  >
                    <span>{formData.birthMonth || '월'}</span>
                    <ChevronIcon $isOpen={dropdownStates.month}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ChevronIcon>
                  </DropdownButton>
                  {dropdownStates.month && !isPhoneVerified && (
                    <DropdownMenu>
                      {months.map(month => (
                        <DropdownItem
                          key={month}
                          onClick={() => handleDropdownSelect('month', month.toString())}
                        >
                          {month}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownContainer>

                <DropdownContainer>
                  <DropdownButton 
                    type="button"
                    onClick={() => !isPhoneVerified && handleDropdownToggle('day')}
                    $isOpen={dropdownStates.day}
                    $hasValue={!!formData.birthDay}
                    disabled={isPhoneVerified}
                  >
                    <span>{formData.birthDay || '일'}</span>
                    <ChevronIcon $isOpen={dropdownStates.day}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ChevronIcon>
                  </DropdownButton>
                  {dropdownStates.day && !isPhoneVerified && (
                    <DropdownMenu>
                      {days.map(day => (
                        <DropdownItem
                          key={day}
                          onClick={() => handleDropdownSelect('day', day.toString())}
                        >
                          {day}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownContainer>
              </BirthContainer>
            </FormGroup>

            {/* 휴대폰 인증 */}
            <FormGroup>
              <SectionTitle>휴대폰 본인인증</SectionTitle>
              <SectionDescription>본인 명의의 휴대폰으로 실명 인증을 하실 수 있습니다.</SectionDescription>
              <CampusPhoneVerification 
                name={formData.name}
                birthYear={formData.birthYear}
                birthMonth={formData.birthMonth}
                birthDay={formData.birthDay}
                onVerificationComplete={handlePhoneVerificationComplete}
                disabled={isPhoneVerified}
              />
            </FormGroup>
          </FormSection>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
};

// 스타일 컴포넌트들
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(23, 23, 25, 0.52);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999 !important;
`;

const ModalContainer = styled.div`
  width: 640px;
  max-width: 1060px;
  min-width: 320px;
  max-height: 760px;
  background: white;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 16px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  backdrop-filter: blur(32px);
`;

const HeaderSpacer = styled.div`
  flex: 1;
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #171719;
  
  &:hover {
    background: rgba(23, 23, 25, 0.08);
  }
`;

const ModalContent = styled.div`
  /* padding: 32px; */
  padding-bottom: 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  overflow-y: auto;
  max-height: 600px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h2`
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38px;
  margin: 0;
`;

const Subtitle = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const FormSection = styled.div`
  width: 480px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const FormInput = styled.input`
  padding: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  line-height: 24px;
  outline: none;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &:focus {
    border-color: #448181;
  }
  
  &.verified {
    background-color: #f8f9fa;
    border-color: #00BF40;
  }
  
  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

// 생년월일 드롭다운 스타일 (회원가입과 동일)
const BirthContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const DropdownContainer = styled.div`
  position: relative;
  flex: 1;
`;

const DropdownButton = styled.button<{ $isOpen?: boolean; $hasValue?: boolean; disabled?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  background: ${props => props.disabled ? '#f8f9fa' : 'white'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 16px;
  color: ${props => props.$hasValue ? '#171719' : 'rgba(55, 56, 60, 0.28)'};
  outline: none;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  
  &:focus {
    border-color: #448181;
  }
  
  ${props => props.disabled && `
    border-color: #00BF40;
  `}
`;

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  color: rgba(55, 56, 60, 0.61);
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 4px;
`;

const DropdownItem = styled.div`
  padding: 12px;
  cursor: pointer;
  font-size: 16px;
  color: #171719;
  
  &:hover {
    background: rgba(68, 129, 129, 0.08);
  }
  
  &:first-child {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }
  
  &:last-child {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: rgba(46, 47, 51, 0.88);
  margin-bottom: 8px;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  line-height: 1.429em;
  color: rgba(55, 56, 60, 0.61);
  margin: 0 0 16px 0;
`;

export default BasicInfoEditModal;
